import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, Note } from '@card-software/library';
import {
  AddChemicalLineCommand,
  AddChemicalLotItemCommand,
  DispatchInstruction,
  DispatchInstructionInsert,
  DispatchInstructionStatus,
  FabricCustomization,
} from '../../models/dispatch-instruction/dispatch-instruction';
import { LotItem } from '../../models/inventory/lot-item';
import { DispatchInstructionSearchCommand } from '../../models/dispatch-instruction/dispatch-instruction-search';
import { DispatchInstructionSearchResult } from '../../models/dispatch-instruction/dispatch-instruction-search-result';
import { DispatchEntryCommand } from '../../models/dispatch-instruction/dispatch-entry-command';
import {
  AvailableRollsQuery,
  AvailableRollsResponse,
  BGRADE_ROLLS_PAGE_SIZE,
  BGradeRollsPageResponse,
} from '../../models/dispatch-instruction/available-rolls';
import { getSkipperErrorHeader } from '../../utils/error-header.util';
import { DispatchInstructionShippingCommand } from '../../models/dispatch-instruction/dispatch-instruction-shipping-command';
import { DispatchInstructionBillingCommand } from '../../models/dispatch-instruction/dispatch-instruction-billing-command';
import { DispatchInstructionExtraToleranceCommand } from '../../models/dispatch-instruction/dispatch-instruction-extra-tolerance-command';

@Injectable({
  providedIn: 'root',
})
export class DispatchInstructionProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/dispatch-instruction';

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<DispatchInstruction>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}`, { headers });
  }

  public search(
    command: DispatchInstructionSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<DispatchInstructionSearchResult>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }

    return this.httpClient.post<ApiResponse<DispatchInstructionSearchResult>>(url, command);
  }

  /**
   * Get available rolls for dispatch by work order references
   */
  public getAvailableRolls(query: AvailableRollsQuery): Observable<ApiResponse<AvailableRollsResponse>> {
    return this.httpClient.post<ApiResponse<AvailableRollsResponse>>(`${this.baseUrl}/available-rolls`, query);
  }

  /**
   * Get a page of available B-Grade rolls. Unlike the other two flows this one is paginated and
   * searched server-side, since the whole B-Grade set is too large to filter in the browser.
   * `search` matches roll numbers or Production Card numbers over the entire dataset.
   */
  public getBGradeAvailableRolls(
    pageNumber = 1,
    pageSize = BGRADE_ROLLS_PAGE_SIZE,
    search?: string,
  ): Observable<ApiResponse<BGradeRollsPageResponse>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);

    const term = search?.trim();
    if (term) {
      params = params.set('search', term);
    }

    return this.httpClient.get<ApiResponse<BGradeRollsPageResponse>>(`${this.baseUrl}/bgrade-rolls`, { params });
  }

  public getGreigeOnlyAvailableRolls(query: AvailableRollsQuery): Observable<ApiResponse<AvailableRollsResponse>> {
    return this.httpClient.post<ApiResponse<AvailableRollsResponse>>(`${this.baseUrl}/greige-only-rolls`, query);
  }

  /**
   * Create a new dispatch instruction
   */
  public create(body: DispatchInstructionInsert): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}`, body);
  }

  public upsertEntry(id: string, command: DispatchEntryCommand): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/entry`, command);
  }

  public removeEntry(id: string, lineId: string): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.delete<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/entry/${lineId}`);
  }

  public addRollsToEntry(id: string, lineId: string, rollNumbers: string[]): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/entry/${lineId}/rolls`, rollNumbers);
  }

  public removeRollsFromEntry(id: string, lineId: string, rollNumbers: string[]): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.request<ApiResponse<DispatchInstruction>>('delete', `${this.baseUrl}/${id}/entry/${lineId}/rolls`, {
      body: rollNumbers,
    });
  }

  public updateStatus(id: string, newStatus: DispatchInstructionStatus): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/status`, newStatus);
  }

  public scanRoll(id: string, rollNumber: string): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/scan-roll`, JSON.stringify(rollNumber), {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  public updateNotes(id: string, notes: Note[]): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.patch<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/notes`, notes);
  }

  public updateShippingInfo(id: string, command: DispatchInstructionShippingCommand): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.patch<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/shipping-info`, command);
  }

  public updateBillingInfo(id: string, command: DispatchInstructionBillingCommand): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.patch<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/billing-info`, command);
  }

  public updateDateAndPriority(id: string, date: string, priority: number): Observable<ApiResponse<DispatchInstruction>> {
    const command = { date, priority };
    return this.httpClient.patch<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/date-and-priority`, command);
  }

  public deleteDateAndPriority(id: string): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.delete<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/date-and-priority`);
  }

  /**
   * Download all export documents (Packing List, Commercial Invoice, Shipping Instructions, CAFTA) as ZIP
   * @param id Dispatch instruction ID
   * @param isPreInvoice If true, the Commercial Invoice will be generated as pre-invoice with watermark
   */
  public downloadAllDocuments(id: string, isPreInvoice = false): Observable<Blob> {
    const url = `${environment.proxyUrls.inventoryApi}/api/v1/report/all-documents/${id}?isPreInvoice=${isPreInvoice}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  public downloadPackingList(id: string, includePrices = true): Observable<Blob> {
    const url = `${environment.proxyUrls.inventoryApi}/api/v1/report/packing-list/${id}?includePrices=${includePrices}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  public downloadCommercialInvoice(id: string, isPreInvoice = false): Observable<Blob> {
    const url = `${environment.proxyUrls.inventoryApi}/api/v1/report/commercial-invoice/${id}?isPreInvoice=${isPreInvoice}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  public downloadShippingInstructions(id: string): Observable<Blob> {
    const url = `${environment.proxyUrls.inventoryApi}/api/v1/report/shipping-instructions/${id}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  public downloadCaftaCertificate(id: string): Observable<Blob> {
    const url = `${environment.proxyUrls.inventoryApi}/api/v1/report/cafta-certificate/${id}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  /**
   * Download a single AFFD (Affidavit of Origin) for one Client Product Order Number
   */
  public downloadAffd(id: string, clientProductOrderNumber: string): Observable<Blob> {
    const url = `${environment.proxyUrls.inventoryApi}/api/v1/report/affd/${id}?clientProductOrderNumber=${encodeURIComponent(clientProductOrderNumber)}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  /**
   * Update fabric customizations for a dispatch instruction
   * @param id Dispatch instruction ID
   * @param fabricCustomizations List of fabric customizations (UnitPrice and AliasReference per fabric)
   */
  public updateExtraTolerance(id: string, command: DispatchInstructionExtraToleranceCommand): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.patch<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/extra-tolerance`, command);
  }

  public updateFabricCustomizations(id: string, fabricCustomizations: FabricCustomization[]): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.patch<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/fabric-customizations`, {
      fabricCustomizations,
    });
  }

  // ============== Chemical dispatch (Pride Chemicals) ==============
  // Create/commit go through the generic create() + updateStatus(Completed) — no parallel endpoints.

  // dispatchId keeps the given dispatch's own assignments in the result; lots claimed by any other
  // non-cancelled chemical dispatch are excluded.
  public getAvailableChemicalLotItems(clientReference: string, dispatchId?: string): Observable<ApiResponse<LotItem[]>> {
    let params = `?clientReference=${encodeURIComponent(clientReference)}`;
    if (dispatchId) {
      params += `&dispatchId=${encodeURIComponent(dispatchId)}`;
    }
    return this.httpClient.get<ApiResponse<LotItem[]>>(`${this.baseUrl}/chemical/available-lot-items${params}`);
  }

  public addChemicalLine(id: string, command: AddChemicalLineCommand): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/chemical/lines`, command);
  }

  public addChemicalLotItem(id: string, command: AddChemicalLotItemCommand): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.post<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/chemical/lot-items`, command);
  }

  public removeChemicalLotItem(id: string, lineId: string, lotItemNumber: string): Observable<ApiResponse<DispatchInstruction>> {
    const params = `?lineId=${encodeURIComponent(lineId)}`;
    return this.httpClient.delete<ApiResponse<DispatchInstruction>>(
      `${this.baseUrl}/${id}/chemical/lot-items/${encodeURIComponent(lotItemNumber)}${params}`,
    );
  }

  public removeChemicalLine(id: string, lineId: string): Observable<ApiResponse<DispatchInstruction>> {
    return this.httpClient.delete<ApiResponse<DispatchInstruction>>(`${this.baseUrl}/${id}/chemical/lines/${encodeURIComponent(lineId)}`);
  }
}
